/**
 * Vendor Service
 *
 * Provides TanStack Query hooks for vendor management operations including:
 * - Fetching paginated vendor lists with search and filters
 * - Retrieving individual vendor details with associated contracts
 * - Creating, updating, and deleting vendors
 *
 * All hooks follow TanStack Query best practices with proper query keys,
 * error handling, and cache invalidation.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryOptions } from '@tanstack/react-query';
import { api } from './api';
import type {
  VendorListItem,
  VendorDetail,
  CreateVendor,
  UpdateVendor,
  VendorContractSummary,
  PagedResult,
  QueryFilters,
} from '../types';

/**
 * Query keys for vendor-related queries
 *
 * Centralized query key management for consistent caching and invalidation
 */
export const vendorKeys = {
  all: ['vendors'] as const,
  lists: () => [...vendorKeys.all, 'list'] as const,
  list: (filters: VendorListFilters) => [...vendorKeys.lists(), filters] as const,
  details: () => [...vendorKeys.all, 'detail'] as const,
  detail: (id: string) => [...vendorKeys.details(), id] as const,
  contracts: (id: string) => [...vendorKeys.all, 'contracts', id] as const,
};

/**
 * Vendor list filters interface
 *
 * Extends base QueryFilters with vendor-specific filter options
 */
export interface VendorListFilters extends QueryFilters {
  /** Filter by active status */
  isActive?: boolean;
  /** Minimum criticality rating (1-5) */
  minCriticalityRating?: number;
}

/**
 * Fetch vendors list with pagination and filters
 *
 * @param filters - Query parameters for filtering, sorting, and pagination
 * @returns TanStack Query hook for vendor list
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useVendors({
 *   search: 'Microsoft',
 *   isActive: true,
 *   pageNumber: 1,
 *   pageSize: 25
 * });
 * ```
 */
export const useVendors = (filters: VendorListFilters = {}) => {
  return useQuery({
    queryKey: vendorKeys.list(filters),
    queryFn: async () => {
      const params = new URLSearchParams();

      if (filters.search) params.append('search', filters.search);
      if (filters.isActive !== undefined) params.append('isActive', String(filters.isActive));
      if (filters.minCriticalityRating) params.append('minCriticalityRating', String(filters.minCriticalityRating));
      if (filters.sortBy) params.append('sortBy', filters.sortBy);
      if (filters.sortOrder) params.append('sortOrder', filters.sortOrder);
      if (filters.pageNumber) params.append('pageNumber', String(filters.pageNumber));
      if (filters.pageSize) params.append('pageSize', String(filters.pageSize));

      const queryString = params.toString();
      const url = `/vendors${queryString ? `?${queryString}` : ''}`;

      return api.get<PagedResult<VendorListItem>>(url);
    },
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Fetch single vendor by ID with full details and contracts
 *
 * @param id - Vendor identifier
 * @param options - Additional TanStack Query options
 * @returns TanStack Query hook for vendor detail
 *
 * @example
 * ```tsx
 * const { data: vendor, isLoading } = useVendor('123');
 * ```
 */
export const useVendor = (
  id: string,
  options?: Omit<UseQueryOptions<VendorDetail>, 'queryKey' | 'queryFn'>
) => {
  return useQuery({
    queryKey: vendorKeys.detail(id),
    queryFn: () => api.get<VendorDetail>(`/vendors/${id}`),
    staleTime: 60000, // 1 minute
    ...options,
  });
};

/**
 * Fetch contracts associated with a vendor
 *
 * @param id - Vendor identifier
 * @returns TanStack Query hook for vendor contracts
 *
 * @example
 * ```tsx
 * const { data: contracts } = useVendorContracts('123');
 * ```
 */
export const useVendorContracts = (id: string) => {
  return useQuery({
    queryKey: vendorKeys.contracts(id),
    queryFn: () => api.get<VendorContractSummary[]>(`/vendors/${id}/contracts`),
    staleTime: 30000, // 30 seconds
  });
};

/**
 * Create a new vendor
 *
 * Automatically invalidates vendor list queries on success
 *
 * @returns TanStack Query mutation hook
 *
 * @example
 * ```tsx
 * const createVendor = useCreateVendor();
 *
 * const handleSubmit = async (data: CreateVendor) => {
 *   try {
 *     const vendor = await createVendor.mutateAsync(data);
 *     navigate(`/vendors/${vendor.id}`);
 *   } catch (error) {
 *     console.error('Failed to create vendor:', error);
 *   }
 * };
 * ```
 */
export const useCreateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateVendor) => api.post<VendorDetail>('/vendors', data),
    onSuccess: () => {
      // Invalidate all vendor list queries
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() });
    },
  });
};

/**
 * Update an existing vendor
 *
 * Automatically invalidates relevant queries on success
 *
 * @returns TanStack Query mutation hook
 *
 * @example
 * ```tsx
 * const updateVendor = useUpdateVendor();
 *
 * const handleUpdate = async (id: string, data: UpdateVendor) => {
 *   try {
 *     await updateVendor.mutateAsync({ id, data });
 *   } catch (error) {
 *     console.error('Failed to update vendor:', error);
 *   }
 * };
 * ```
 */
export const useUpdateVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateVendor }) =>
      api.put<VendorDetail>(`/vendors/${id}`, data),
    onSuccess: (updatedVendor) => {
      // Invalidate vendor lists
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() });

      // Invalidate specific vendor detail
      queryClient.invalidateQueries({ queryKey: vendorKeys.detail(updatedVendor.id) });

      // Invalidate vendor contracts
      queryClient.invalidateQueries({ queryKey: vendorKeys.contracts(updatedVendor.id) });
    },
  });
};

/**
 * Delete a vendor
 *
 * Automatically invalidates relevant queries on success
 *
 * @returns TanStack Query mutation hook
 *
 * @example
 * ```tsx
 * const deleteVendor = useDeleteVendor();
 *
 * const handleDelete = async (id: string) => {
 *   if (confirm('Are you sure you want to delete this vendor?')) {
 *     try {
 *       await deleteVendor.mutateAsync(id);
 *       navigate('/vendors');
 *     } catch (error) {
 *       console.error('Failed to delete vendor:', error);
 *     }
 *   }
 * };
 * ```
 */
export const useDeleteVendor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/vendors/${id}`),
    onSuccess: (_, deletedId) => {
      // Invalidate vendor lists
      queryClient.invalidateQueries({ queryKey: vendorKeys.lists() });

      // Remove specific vendor from cache
      queryClient.removeQueries({ queryKey: vendorKeys.detail(deletedId) });

      // Remove vendor contracts from cache
      queryClient.removeQueries({ queryKey: vendorKeys.contracts(deletedId) });
    },
  });
};

/**
 * Fetch all active vendors for use in dropdowns
 *
 * Returns a simplified list of active vendors optimized for form selects.
 * Data is cached for 10 minutes since vendor lists don't change frequently.
 *
 * @returns TanStack Query hook for active vendors
 *
 * @example
 * ```tsx
 * const { data: vendors, isLoading } = useActiveVendors();
 *
 * <TextField select>
 *   {vendors?.map(vendor => (
 *     <MenuItem key={vendor.id} value={vendor.id}>
 *       {vendor.name}
 *     </MenuItem>
 *   ))}
 * </TextField>
 * ```
 */
export const useActiveVendors = () => {
  return useQuery({
    queryKey: [...vendorKeys.lists(), { isActive: true, simple: true }],
    queryFn: async () => {
      const response = await api.get<PagedResult<VendorListItem>>('/vendors', {
        params: {
          isActive: true,
          pageSize: 100, // API max is 100
        },
      });

      // Map to simple structure for dropdowns
      return response.items.map((vendor) => ({
        id: vendor.id,
        name: vendor.name,
      }));
    },
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
};
